import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DateInput } from 'semantic-ui-calendar-react'
import { Form } from 'semantic-ui-react'
import { AccountType } from '~/api/services/milkMoneyAccounting'
import ProfileSettingsService from '~/api/services/profileSettings'
import Dialog from '~/components/Dialog'
import notify from '~/components/Notify'
import TwoFaComp from '~/components/TwoFaComp'
import { useAuth } from '~/routes/components/AuthProvider'
import { KeyLabelType } from '~/types'
import { handleErrors, parseDate } from '~/utils/helpers'

type BankDataType = {
  date: string,
  text: string,
  milkMoneyAccountId: string | number,
  code?: string
}

interface CreateBankFileProps {
  openModal: boolean
  handleClose: (submitted?) => void
  selectedMonth: KeyLabelType
  handleSubmit: (data: BankDataType) => void
  accounts: AccountType[]
  header?: string
}

const CreateBankFile: React.FC<CreateBankFileProps> = (props) => {
  const { t } = useTranslation()
  const { openModal, handleClose, selectedMonth, handleSubmit, accounts } = props
  const { systemData: { languages, logged: { languageId } }, systemData } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)
  const tfaGenerateBankFile = systemData?.roleSettings?.tfaGenerateBankFile === 1
  const tfaType = systemData?.logged?.tfaType

  const [correctTfaCode, setCorrectTfaCode] = useState(false)
  const [tfaCode, setTfaCode] = useState('')

  const [openExportBankModal, setOpenExportBankModal] = useState(openModal)
  const [isBusy, setIsBusy] = useState(false)
  const [date, setDate] = useState('')
  const [text, setText] = useState('')
  const [milkMoneyAccountId, setMilkMoneyAccountId] = useState(null)

  useEffect(() => {
    if (tfaGenerateBankFile && openModal) {
      handleSendCode()
    }
    setOpenExportBankModal(openModal)
    setDate('')
    setText('')
    setMilkMoneyAccountId(null)
  }, [openModal])

  async function handleSendCode() {
    if (tfaType === 'email' || tfaType === 'sms') {
      setIsBusy(true)
      try {
        await ProfileSettingsService.sendCode()
        notify(t('Code sent'), 'success')
      } catch (error) {
        handleErrors(error)
      }
      setIsBusy(false)
    }
  }

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  function validateCreateBankForm() {
    if (!date || text === '' || !milkMoneyAccountId) {
      return true
    }
    return false
  }

  function handleSubmitBankData() {
    if (tfaCode) {
      handleSubmit({
        date: parseDate(date),
        text,
        code: tfaCode,
        milkMoneyAccountId
      })
    } else {
      handleSubmit({ date: parseDate(date), text, milkMoneyAccountId })
    }
  }

  return <Dialog
    isDialogOpen={openExportBankModal}
    handleClose={() => handleClose()}
    isLoading={isBusy}
    textHeader={props.header || t('SEPA XML for {{month}}', { month: selectedMonth?.label })}
    textSubmit={t('SEPA XML')}
    noScrollingContent
    textClose={t('Cancel')}
    disableSubmit={isBusy || tfaGenerateBankFile ? !correctTfaCode : validateCreateBankForm()}
    handleSubmit={() => handleSubmitBankData()}
    textContent={<>
      <Form>
        <DateInput
          name='date'
          autoComplete='off'
          placeholder={t('Transfer date')}
          minDate={dayjs(new Date()).add(1, 'day').format('DD.MM.YYYY')}
          closable
          clearIcon={'close'}
          onKeyPress={handleKeyPress}
          clearable
          localization={languageLocal.code}
          closeOnMouseLeave={false}
          dateFormat={'DD.MM.YYYY'}
          className={'datePicker'}
          value={date}
          iconPosition='left'
          onChange={(event, { name, value }) => setDate(value)}
        />
        <Form.TextArea
          placeholder={t('Purpose of use')}
          value={text}
          rows={5}
          //@ts-ignore
          onChange={(e, data) => setText(data.value)}
        />
        <Form.Select
          label={t('Bank account')}
          placeholder={t('Please select bank account')}
          options={accounts.map(account => ({ text: `${account.name} (${account.iban})`, value: account.id }))}
          onChange={(_, data) => setMilkMoneyAccountId(data.value)}
          value={milkMoneyAccountId}
        />
      </Form>
      {tfaGenerateBankFile && <TwoFaComp
        onChangeTfaCode={(code) => setTfaCode(code)}
        onChangeCorrectTfaCode={(correctTfaCode) => setCorrectTfaCode(correctTfaCode)}
      />
      }
    </>
    }
  />
}
export default CreateBankFile